import { ArrowCounterClockwise } from "@phosphor-icons/react";
import { useReducer } from "react";

import EmptyPlaceholder from "common/components/EmptyPlaceholder";
import ErrorPlaceholder from "common/components/ErrorPlaceholder";
import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import type { ProfileSearchParams } from "common/types";

import { useCampaignContext } from "../../../../../context/CampaignContext";
import useProfilesPreview from "../../../../../datahooks/useProfilesPreview";
import useTargetingMutations from "../../../../../datahooks/useTargetingMutations";
import SectionHeading from "../../../SectionHeading";
import Stepper from "../../Stepper";
import StepperNextButton from "../../StepperNextButton";
import MinerAccountSelector from "../MinerAccountSelector";
import PasteUrlTargeting from "./PasteUrlTargeting";
import PreviewResults from "./PreviewResults";
import SearchParams from "./SearchParams";
import { SearchParamsProvider } from "./SearchParamsContext";
import TargetingInfo from "./TargetingInfo";

import ErrorPlaceholderImage from "assets/images/empty-placeholders/error.png";

const defaultSearchParams: ProfileSearchParams = {
  keywords: [],
  locations: [],
  industries: [],
  current_companies: [],
  past_companies: [],
  followers_of: null,
  schools: [],
  languages: [],
  skills: [],
};

export default function SearchPreview({
  initialParams,
}: {
  initialParams: ProfileSearchParams | undefined;
}) {
  const { outreach_type: outreachType } = useCampaignContext();
  const isDrip = outreachType === "drip";

  const [searchParams, setSearchParams] = useReducer(
    (
      prevState: ProfileSearchParams,
      newState: Partial<ProfileSearchParams>,
    ) => ({
      ...prevState,
      ...newState,
    }),
    initialParams || defaultSearchParams,
  );

  const { setTargeting, isSettingTargeting } = useTargetingMutations();

  const hasSearchParams = Object.values(searchParams).some((param) =>
    Array.isArray(param) ? param.length : param,
  );

  const {
    profiles,
    isLoadingProfiles,
    totalCount,
    isLimitExceeded,
    isProfilesPreviewError,
    refetchProfilesPreview,
  } = useProfilesPreview({
    searchParams,
  });

  const resetSearchParamsButton = hasSearchParams && (
    <Button
      size="sm"
      variant="secondary-black"
      className="max-lg:ml-auto"
      leftIcon={<ArrowCounterClockwise />}
      onClick={() => setSearchParams(defaultSearchParams)}
    >
      Reset all
    </Button>
  );

  let mainContent: React.ReactNode;
  if (isProfilesPreviewError) {
    mainContent = (
      <ErrorPlaceholder
        errorMessage="Error loading search preview results"
        onRetry={refetchProfilesPreview}
      />
    );
  } else if (isLimitExceeded) {
    mainContent = (
      <EmptyPlaceholder
        imageSrc={ErrorPlaceholderImage}
        title="LinkedIn search limit reached"
        subtitle="Try again later, or select a different search account"
        actionComponent={
          <Button variant="secondary-black" asChild>
            <a
              href="https://help.aimfox.com/en/articles/13619474-linkedin-search-limits"
              target="_blank"
              rel="noopener noreferrer"
            >
              Read more
            </a>
          </Button>
        }
      />
    );
  } else {
    mainContent = (
      <PreviewResults profiles={profiles} isLoading={isLoadingProfiles} />
    );
  }

  return (
    <>
      <SearchParamsProvider value={{ searchParams, setSearchParams }}>
        {/* Heading */}
        <section className="lg:border-b lg:border-dashed lg:border-b-black-200 flex flex-col items-center justify-between gap-4 lg:flex-row lg:pb-4">
          <SectionHeading
            title="Targeting Setup"
            subtitle="Find targets for this campaign by adding relevant keywords and
              filters. You can add up to 6 keywords and unlimited filters"
          />

          <div className="flex w-full items-center justify-end gap-x-2.5 lg:gap-x-1.5">
            {!isDrip && (
              <>
                <div className="flex items-center">
                  <span className="mr-1 inline-flex text-button-xs">
                    Search account:
                  </span>

                  <MinerAccountSelector className="lg:w-[130px]" size="sm" />
                </div>

                <span className="h-8 w-px bg-black-200" />
              </>
            )}

            <PasteUrlTargeting hasSearchParams={hasSearchParams} />
          </div>

          <RenderIf condition={hasSearchParams}>
            <Button
              size="sm"
              variant="secondary-black"
              className="ml-auto lg:hidden"
              leftIcon={<ArrowCounterClockwise />}
              onClick={() => setSearchParams(defaultSearchParams)}
            >
              Reset all
            </Button>
          </RenderIf>
        </section>

        {/* Filters */}
        <SearchParams
          totalCount={totalCount}
          isLoadingProfiles={isLoadingProfiles}
          resetSearchParamsButton={resetSearchParamsButton}
        />

        {mainContent}
      </SearchParamsProvider>

      <RenderIf condition={profiles?.length > 0}>
        <TargetingInfo />
      </RenderIf>

      <Stepper
        selectedIndex={0}
        nextButtonComponent={
          <StepperNextButton
            disabled={!profiles || !profiles.length}
            isLoading={isSettingTargeting}
            onClick={() => setTargeting({ campaignTargeting: searchParams })}
          />
        }
      />
    </>
  );
}
